import { useI18nContext } from "@i18n/i18n-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { CardDescription, CardTitle } from "@shadcn/ui/card";
import { DrawerTitle } from "@shadcn/ui/drawer";
import { useNavigate, useLocation, useSearch } from "@tanstack/react-router";
import { formatToCurrency } from "@utils/currency";
import { isMedicalCategory } from "@utils/consultation";
import { MessageSquareText } from "lucide-react";
import { useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { SncProductDrawerFragment$key } from "./__generated__/SncProductDrawerFragment.graphql";
import { AddToBagButton } from "./ProductDrawer/AddToBagButton";

const fragment = graphql`
	fragment SncProductDrawerFragment on SncProduct {
		id
		priceAmount
		stock
		product {
			id
			name
			description
			photo {
				id
				variant(variant: PUBLIC)
			}
			brand {
				id
				name
				organization {
					id
					name
					imprint
				}
			}
			category {
				id
				name
				i18N
			}
		}
	}
`;

type SncProductDrawerProps = {
	hubId: string;
	product: SncProductDrawerFragment$key;
	onClose?: () => void;
};

export const SncProductDrawer = (props: SncProductDrawerProps) => {
	const { hubId, product: productKey, onClose } = props;

	const { id, product, priceAmount, stock } = useFragment(fragment, productKey);
	const [isOpen, setIsOpen] = useState(true);
	const navigate = useNavigate();
	const location = useLocation();
	const searchParams = useSearch({ strict: false }) as Record<string, unknown>;
	const i18n = useI18nContext().LL["new-ui"];
	const isMedical = isMedicalCategory(product.category);
	const hubMetaName = location.pathname.split("/")[1];

	return (
		<KeyboardAwareDrawer
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open);
				if (!open) {
					if (onClose) {
						onClose();
					} else {
						const { sncProductId: _, ...rest } = searchParams;
						navigate({
							to: location.pathname,
							search: rest as Record<string, string>,
							replace: true,
						});
					}
				}
			}}
			shouldScaleBackground
		>
			<KeyboardAwareDrawerContent className="max-h-full! h-full overflow-hidden">
				<VisuallyHidden>
					<DrawerTitle>Product Detail Page {product.name}</DrawerTitle>
				</VisuallyHidden>
				<div className="space-y-4 -mt-6 overflow-y-scroll pointer-events-all pb-22">
					<img src={product.photo?.variant ?? "https://ui.shadcn.com/placeholder.svg"} alt="" className="object-cover w-full h-88" />
					<div className="px-6 py-4 space-y-1.5">
						<CardTitle className="text-2xl">{product.name}</CardTitle>
						<CardTitle>{formatToCurrency(priceAmount)}</CardTitle>
						<CardDescription className="whitespace-pre-wrap">{product.description}</CardDescription>
					</div>
					{isMedical && hubMetaName && (
						<button
							type="button"
							onClick={() => {
								setIsOpen(false);
								navigate({ to: "/$hubMetaName/consultation", params: { hubMetaName } });
							}}
							className="flex w-full items-center gap-2 px-6 text-left"
						>
							<MessageSquareText className="size-5 shrink-0 text-primary" />
							<span className="font-breit text-base leading-none text-primary">{i18n.CONSULTATION_PRODUCT_LINK()}</span>
						</button>
					)}
					{product.brand.organization.imprint && (
						<div className="px-6 py-4">
							<h3 className="text-lg font-semibold mb-2">{i18n.PRODUCT_IMPRINT_TITLE()}</h3>
							<p className="text-sm text-muted-foreground whitespace-pre-line">{product.brand.organization.imprint}</p>
						</div>
					)}
				</div>
				<div className="fixed bottom-0 left-0 right-0 m-6 md:static md:w-full">
					<AddToBagButton
						hubProductId={id}
						price={priceAmount}
						stock={stock}
						onClick={() => {
							setIsOpen(false);
							if (onClose) {
								setTimeout(onClose, 300);
							}
						}}
						disabled={stock === 0}
					/>
				</div>
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
};
